import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Contact, Note, Task, Activity } from '../../types';
import { decodeId } from '../../utils/hashids';
import {
  fetchContact, deleteContact, createNote,
  fetchNotes, fetchTasks, fetchActivities,
  deleteNote, updateTask, createTask,
} from '../../services/api';
import ContactForm from './ContactForm';

type Tab = 'overview' | 'timeline';
type TLFilter = 'all' | 'notes' | 'tasks' | 'status';

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const timeAgo = (iso?: string | null) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="ld-info-row">
    <span className="ld-info-label">{label}</span>
    <span className="ld-info-value">{value || <em className="ld-empty">—</em>}</span>
  </div>
);

const ACTIVITY_ICON: Record<string, string> = {
  created: '✦', updated: '✎', status_changed: '⇄', assigned: '👤',
  note_added: '📝', task_added: '☑', task_completed: '✓', voice_call: '📞',
};

type FeedItem =
  | { kind: 'activity'; ts: string; data: Activity }
  | { kind: 'note';     ts: string; data: Note }
  | { kind: 'task';     ts: string; data: Task };

const TL_FILTERS: { key: TLFilter; label: string }[] = [
  { key: 'all',    label: 'All' },
  { key: 'status', label: 'Activity' },
  { key: 'notes',  label: 'Notes' },
  { key: 'tasks',  label: 'Tasks' },
];

/* ── Contact Timeline ───────────────────────────────────────────────────── */
const ContactTimeline: React.FC<{ contactId: number; refreshKey?: number }> = ({ contactId, refreshKey = 0 }) => {
  const [items, setItems]     = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState<TLFilter>('all');
  const [addingTask, setAddingTask]   = useState(false);
  const [taskTitle, setTaskTitle]     = useState('');
  const [taskDue, setTaskDue]         = useState('');
  const [taskPriority, setTaskPriority] = useState<'low'|'medium'|'high'>('medium');
  const [savingTask, setSavingTask]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchActivities('contacts', contactId) as Promise<Activity[]>,
      fetchNotes('contacts', contactId) as Promise<Note[]>,
      fetchTasks('contacts', contactId) as Promise<Task[]>,
    ]).then(([activities, notes, tasks]) => {
      const feed: FeedItem[] = [
        ...activities.map(a => ({ kind: 'activity' as const, ts: a.created_at, data: a })),
        ...notes.map(n => ({ kind: 'note' as const, ts: n.created_at, data: n })),
        ...tasks.map(t => ({ kind: 'task' as const, ts: t.created_at, data: t })),
      ];
      feed.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
      setItems(feed);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDeleteNote = async (noteId: number) => {
    await deleteNote('contacts', contactId, noteId);
    setItems(prev => prev.filter(i => !(i.kind === 'note' && i.data.id === noteId)));
  };

  const handleToggleTask = async (taskId: number, isDone: boolean) => {
    const updated = await updateTask('contacts', contactId, taskId, { is_done: !isDone });
    setItems(prev => prev.map(i => i.kind === 'task' && i.data.id === taskId ? { ...i, data: updated } : i));
  };

  const handleAddTask = async () => {
    if (!taskTitle.trim()) return;
    setSavingTask(true);
    try {
      const created: Task = await createTask('contacts', contactId, {
        title: taskTitle.trim(), due_date: taskDue || undefined, priority: taskPriority,
      });
      setItems(prev => [{ kind: 'task', ts: created.created_at, data: created }, ...prev]);
      setTaskTitle(''); setTaskDue(''); setTaskPriority('medium'); setAddingTask(false);
    } catch {} finally { setSavingTask(false); }
  };

  const filtered = items.filter(i => {
    if (filter === 'all')    return true;
    if (filter === 'notes')  return i.kind === 'note';
    if (filter === 'tasks')  return i.kind === 'task';
    if (filter === 'status') return i.kind === 'activity';
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {TL_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1.5px solid',
            borderColor: filter === f.key ? '#7c3aed' : 'var(--border)',
            background: filter === f.key ? '#ede9fe' : '#fff',
            color: filter === f.key ? '#7c3aed' : 'var(--text-muted)', cursor: 'pointer',
          }}>{f.label}</button>
        ))}
        {(filter === 'tasks' || filter === 'all') && !addingTask && (
          <button onClick={() => setAddingTask(true)} style={{
            marginLeft: 'auto', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: '1.5px solid #16a34a', background: '#dcfce7', color: '#166534', cursor: 'pointer',
          }}>+ Add Task</button>
        )}
      </div>

      {addingTask && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 10 }}>New Task</div>
          <input className="form-input" placeholder="Task title *" value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddTask()} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input type="date" className="form-input" value={taskDue} onChange={e => setTaskDue(e.target.value)} style={{ flex: 1 }} />
            <select className="form-input" value={taskPriority} onChange={e => setTaskPriority(e.target.value as any)} style={{ flex: 1 }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => { setAddingTask(false); setTaskTitle(''); }} style={{ fontSize: 13 }}>Cancel</button>
            <button className="btn-primary" onClick={handleAddTask} disabled={savingTask || !taskTitle.trim()} style={{ fontSize: 13, minWidth: 100 }}>
              {savingTask ? 'Saving…' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="spinner">Loading…</div> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>No items yet.</div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {filtered.map((item, idx) => (
              <div key={`${item.kind}-${item.data.id}-${idx}`} style={{ display: 'flex', gap: 16, paddingBottom: 20 }}>
                <div style={{
                  flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, zIndex: 1,
                  background: item.kind === 'note' ? '#fef9c3' : item.kind === 'task' ? '#dcfce7' : '#f1f5f9',
                  border: '2px solid',
                  borderColor: item.kind === 'note' ? '#fde047' : item.kind === 'task' ? '#86efac' : 'var(--border)',
                }}>
                  {item.kind === 'note' ? '📝' : item.kind === 'task' ? (item.data.is_done ? '✓' : '☐') : ACTIVITY_ICON[item.data.type] ?? '·'}
                </div>
                <div style={{ flex: 1, paddingTop: 4 }}>
                  {item.kind === 'activity' && (
                    <div>
                      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5 }}>{item.data.description}</div>
                      {item.data.user && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>by {item.data.user.name}</div>}
                    </div>
                  )}
                  {item.kind === 'note' && (
                    <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap', flex: 1 }}>{item.data.content}</div>
                        <button onClick={() => handleDeleteNote(item.data.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, marginLeft: 8, flexShrink: 0 }}>✕</button>
                      </div>
                      {item.data.user && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>by {item.data.user.name}</div>}
                    </div>
                  )}
                  {item.kind === 'task' && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: item.data.is_done ? '#f0fdf4' : '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                      <button onClick={() => handleToggleTask(item.data.id, item.data.is_done)} style={{
                        flexShrink: 0, width: 20, height: 20, borderRadius: 4, border: '2px solid', cursor: 'pointer',
                        borderColor: item.data.is_done ? '#16a34a' : '#94a3b8',
                        background: item.data.is_done ? '#16a34a' : '#fff', color: '#fff', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{item.data.is_done ? '✓' : ''}</button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, color: 'var(--navy)', fontWeight: 500, textDecoration: item.data.is_done ? 'line-through' : 'none', opacity: item.data.is_done ? 0.6 : 1 }}>
                          {item.data.title}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                          {item.data.due_date && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Due: {item.data.due_date}</span>}
                          {item.data.priority && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                              background: item.data.priority === 'high' ? '#fee2e2' : item.data.priority === 'medium' ? '#fef9c3' : '#f1f5f9',
                              color:      item.data.priority === 'high' ? '#dc2626' : item.data.priority === 'medium' ? '#854d0e' : '#64748b',
                            }}>{item.data.priority}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fmtDate(item.ts)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── ContactDetail ──────────────────────────────────────────────────────── */
const ContactDetail: React.FC = () => {
  const { id: idHash } = useParams<{ id: string }>();
  const numId          = decodeId(idHash) ?? 0;
  const history        = useHistory();

  const [contact, setContact]     = useState<Contact | null>(null);
  const [tab, setTab]             = useState<Tab>('overview');
  const [error, setError]         = useState('');
  const [quickNote, setQuickNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const [showEdit, setShowEdit]   = useState(false);
  const quickNoteRef = useRef<HTMLTextAreaElement>(null);

  const reload = () =>
    fetchContact(numId).then(setContact).catch(() => setError('Contact not found'));

  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHash]);

  if (error)    return <div className="ld-page"><p className="ld-empty">Contact not found.</p></div>;
  if (!contact) return <div className="ld-page"><div className="spinner">Loading…</div></div>;

  const handleQuickNote = async () => {
    if (!quickNote.trim()) return;
    setSavingNote(true);
    try {
      await createNote('contacts', contact.id, quickNote.trim());
      setQuickNote('');
      setNoteCount(n => n + 1);
      if (tab !== 'timeline') setTab('timeline');
    } finally { setSavingNote(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    await deleteContact(contact.id);
    history.push('/contacts');
  };

  return (
    <div className="ld-page">
      <button className="breadcrumb-back" onClick={() => history.push('/contacts')}>
        ← Back to Contacts
      </button>

      <div className="ld-layout">
        {/* ── MAIN COLUMN ─────────────────────────────── */}
        <div className="ld-main">
          {/* Header card */}
          <div className="ld-header-card">
            <div className="ld-header-top">
              <div className="ld-avatar">{initials(contact.name)}</div>
              <div className="ld-hero">
                <div className="ld-hero-row">
                  <h1 className="ld-name">{contact.name}</h1>
                  {contact.is_primary && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed' }}>
                      PRIMARY
                    </span>
                  )}
                </div>
                <div className="ld-company">
                  {[contact.designation, contact.organization?.name].filter(Boolean).join(' · ') || '—'}
                </div>
                <div className="ld-contact-row">
                  {contact.phone && <a className="ld-phone" href={`tel:${contact.phone}`}>{contact.phone}</a>}
                  {contact.email && <span className="ld-email">{contact.email}</span>}
                </div>
              </div>
              <div className="ld-header-meta">
                <span className="ld-time">{timeAgo(contact.updated_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="ld-actions">
              {contact.phone && (
                <a className="ld-btn ld-btn-dial" href={`tel:${contact.phone}`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.01L6.6 10.8z"/></svg>
                  Dial
                </a>
              )}
              {contact.whatsapp_number && (
                <a className="ld-btn ld-btn-wa" href={`https://wa.me/${contact.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.18-.01-.382-.012-.57-.012-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                  WhatsApp
                </a>
              )}
              <button className="ld-btn ld-btn-note" onClick={() => { setTab('timeline'); setTimeout(() => quickNoteRef.current?.focus(), 50); }}>
                + Note
              </button>
              <button className="ld-btn ld-btn-more" onClick={() => setShowEdit(true)}>Edit</button>
              <button className="ld-btn ld-btn-danger" onClick={handleDelete}>Delete</button>
            </div>

            {/* Tabs */}
            <div className="ld-tabs">
              {(['overview', 'timeline'] as Tab[]).map(t => (
                <button key={t} className={`ld-tab${tab === t ? ' ld-tab-active' : ''}`} onClick={() => setTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab bodies */}
          <div className="ld-tab-body">
            {tab === 'overview' && (
              <div className="ld-overview">
                <div className="ld-section">
                  <h3 className="ld-section-title">Contact Info</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Phone"        value={contact.phone} />
                    <InfoRow label="WhatsApp"      value={contact.whatsapp_number} />
                    <InfoRow label="Email"         value={contact.email} />
                    <InfoRow label="Designation"   value={contact.designation} />
                    <InfoRow label="Organization"  value={contact.organization?.name} />
                    <InfoRow label="Primary"       value={contact.is_primary ? 'Yes' : 'No'} />
                  </div>
                </div>
                {contact.notes && (
                  <div className="ld-section">
                    <h3 className="ld-section-title">Notes</h3>
                    <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{contact.notes}</div>
                  </div>
                )}
              </div>
            )}

            {tab === 'timeline' && (
              <div className="ld-section">
                <ContactTimeline contactId={contact.id} refreshKey={noteCount} />
              </div>
            )}
          </div>
        </div>

        {/* ── SIDEBAR ─────────────────────────────────── */}
        <div className="ld-sidebar">
          {/* Organization */}
          <div className="ld-card">
            <div className="ld-card-title">Organization</div>
            {contact.organization ? (
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{contact.organization.name}</div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No organization linked</div>
            )}
          </div>

          {/* Quick Note */}
          <div className="ld-card">
            <div className="ld-card-title">Quick Note</div>
            <textarea ref={quickNoteRef} className="ld-quick-note" placeholder="Type a note and click Save…" rows={3}
              value={quickNote} onChange={e => setQuickNote(e.target.value)} />
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
              onClick={handleQuickNote} disabled={savingNote || !quickNote.trim()}>
              {savingNote ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {showEdit && (
        <ContactForm
          contact={contact}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setContact(updated); setShowEdit(false); }}
        />
      )}
    </div>
  );
};

export default ContactDetail;
